import axios from 'axios';
import { VideoChat } from '~/models/communication';
import CommunicationService from '~/services/CommunicationService';

export const LOCAL_STORAGE_ID = 'wb_video_chats';
export const LOCAL_STORAGE_ID_CURRENT_VIDEO = 'wb_maximized_video_chat';

interface VideoModuleState {
    videos: VideoChat[];
}
interface KeyValue {
    key: string;
    value: string;
}

interface VideoFullName {
    id: string;
    fullName: string;
}

const isAVideoChatObject = (object: any) => {
    return !!object && !!object.id && !!object.url && 'minimized' in object;
};

const parameterParser = (data: Array<KeyValue>): string => {
    if (!data) return '';
    return data.map(item => `${item.key}=${encodeURIComponent(item.value)}`).join('&');
};

const wherebyUrl = (videoUrl: string, fullName: string): string => {
    const params: Array<KeyValue> = [
        {
            key: 'iframeSource',
            value: encodeURIComponent('highered'),
        },
        {
            key: 'video',
            value: encodeURIComponent('off'),
        },
        {
            key: 'displayName',
            value: encodeURIComponent(fullName),
        },
        { key: 'minimal', value: 'on' },
    ];
    return videoUrl.concat('?embed&', parameterParser(params));
};

const addToPersistantStorage = (id: string): void => {
    let ids: Array<string> = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_ID) || JSON.stringify([])
    );
    const index = ids.findIndex(storageId => storageId === id);
    if (index !== -1) return;
    localStorage.setItem(LOCAL_STORAGE_ID, JSON.stringify([...ids, id]));
};

const removeFromPersistantStorage = (localId: string): void => {
    const ids: Array<string> = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_ID) || JSON.stringify([])
    );
    localStorage.setItem(LOCAL_STORAGE_ID, JSON.stringify(ids.filter(id => id != localId)));
};

const createVideoChat = async (
    id: string,
    fullName: string,
    minimized: boolean = false
): Promise<VideoChat> => {
    return getVideoUrlById(id).then((url: string) => {
        const parsedUrl = wherebyUrl(url, fullName);
        const videoChat: VideoChat = { id: id, url: parsedUrl, minimized: minimized };
        addToPersistantStorage(videoChat.id);
        return videoChat;
    });
};

const containsMaximized = (videos: Array<VideoChat>): boolean => {
    const isMaximized: boolean = 0 < videos.filter(v => !v.minimized).length;
    return isMaximized;
};

const getVideoUrlById = async (id: string): Promise<string> =>
    CommunicationService.getWhereByUrl(id).then(res => res.data.url);

export default {
    state: {
        videos: [],
    } as VideoModuleState,
    getters: {
        getVideoCalls: (state: VideoModuleState) => () => state.videos || [],
    },
    mutations: {
        async addNewVideoChat(
            state: VideoModuleState,
            newVideo: VideoFullName
        ): Promise<VideoChat> {
            const index = state.videos.findIndex(v => v.id === newVideo.id);
            if (index !== -1) return state.videos[index];

            state.videos.forEach(video => {
                video.minimized = true;
            });
            return createVideoChat(newVideo.id, newVideo.fullName).then(videoChat => {
                state.videos.push(videoChat);
                return videoChat;
            });
        },
        deleteVideoChat(state: VideoModuleState, videoChatId: string): void {
            const index = state.videos.findIndex(v => v.id === videoChatId);
            if (index !== -1) {
                removeFromPersistantStorage(videoChatId);
                state.videos.splice(index, 1);
            }
        },
        minimizeVideoChat(state: VideoModuleState, videoChatId: string): void {
            const index = state.videos.findIndex(v => v.id === videoChatId);
            if (index !== -1) {
                localStorage.removeItem(LOCAL_STORAGE_ID_CURRENT_VIDEO);
                state.videos[index].minimized = true;
            }
        },
        maximizeVideoChat(state: VideoModuleState, videoChatId: string): void {
            const index = state.videos.findIndex(v => v.id === videoChatId);
            if (index !== -1 && !containsMaximized(state.videos)) {
                localStorage.setItem(LOCAL_STORAGE_ID_CURRENT_VIDEO, state.videos[index].id);
                state.videos[index].minimized = false;
            }
        },
        async getFromPersistantStorage(
            state: VideoModuleState,
            fullName: string
        ): Promise<VideoChat[]> {
            const ids: Array<string> = JSON.parse(
                localStorage.getItem(LOCAL_STORAGE_ID) || JSON.stringify([])
            );
            const maximizedVideoID = localStorage.getItem(LOCAL_STORAGE_ID_CURRENT_VIDEO) || '';

            const promises = ids
                .map(id => {
                    const index = state.videos.findIndex(v => v.id === id);
                    if (index === -1) {
                        return createVideoChat(id, fullName, !(maximizedVideoID === id)).catch(
                            axiosError => {
                                removeFromPersistantStorage(id);
                                return axiosError;
                            }
                        );
                    }
                })
                .filter(p => !!p) as Array<Promise<VideoChat>>;

            return Promise.all(promises).then(videoChatsOrError => {
                videoChatsOrError.forEach((videoChatOrError: void | VideoChat) => {
                    if (!!videoChatOrError && isAVideoChatObject(videoChatOrError)) {
                        state.videos.push(videoChatOrError);
                    }
                });
                return state.videos;
            });
        },
        clear(state: VideoModuleState) {
            state.videos = [];
            localStorage.removeItem(LOCAL_STORAGE_ID);
            localStorage.removeItem(LOCAL_STORAGE_ID_CURRENT_VIDEO);
        },
    },
};
